import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  WifiIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';
import type { StockItem } from '../../types/inventory';
import { STATE_LABELS, STATE_COLORS } from '../../types/inventory';

// Mirror of the backend normaliseMac(): accept loose input (any/no separators,
// any case) and produce the canonical AA:BB:CC:DD:EE:FF the lookup matches on.
function normaliseMac(input: string): string | null {
  const stripped = input.trim().toUpperCase().replace(/[\s:-]/g, '');
  if (!/^[0-9A-F]{12}$/.test(stripped)) return null;
  return stripped.match(/.{2}/g)!.join(':');
}

// States in which registering a build makes sense. Beyond `built` the device
// has left the workshop, so building is no longer applicable.
const BUILDABLE_STATES = new Set(['in_stock', 'in_build', 'built']);

interface AppVersionRow {
  key: string;
  value: string;
}

interface SessionBuild {
  serial: string;
  product: string;
  at: number;
}

export function AdminInventoryBuildStationPage() {
  const qc = useQueryClient();
  const { user } = useAuth();

  const [macInput, setMacInput] = useState('');
  const [found, setFound] = useState<StockItem | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Build form
  const [builtBy, setBuiltBy] = useState('');
  const [gitSha, setGitSha] = useState('');
  const [androidVersion, setAndroidVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [appVersions, setAppVersions] = useState<AppVersionRow[]>([{ key: '', value: '' }]);

  const [sessionBuilds, setSessionBuilds] = useState<SessionBuild[]>([]);

  const macInputRef = useRef<HTMLInputElement>(null);

  // Keep the MAC field focused whenever we're back on the lookup step, so a
  // USB scanner can drive the whole flow hands-free.
  useEffect(() => {
    if (!found) {
      const t = setTimeout(() => macInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [found]);

  const resetForm = useCallback(() => {
    setBuiltBy(user?.name ?? user?.email ?? '');
    setGitSha('');
    setAndroidVersion('');
    setNotes('');
    setAppVersions([{ key: '', value: '' }]);
  }, [user]);

  const lookupMutation = useMutation({
    mutationFn: (mac: string) => api.adminGetStockItemByMac(mac),
    onSuccess: (item) => {
      setFound(item);
      setLookupError(null);
      setMacInput('');
      resetForm();
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      setLookupError(
        status === 404
          ? `No inventory item is registered with MAC ${normaliseMac(macInput)}. Capture the MAC on the item first (Receive Stock or the item's Edit screen).`
          : err?.response?.data?.error ?? 'Lookup failed. Please try again.',
      );
    },
  });

  function handleLookup() {
    const canonical = normaliseMac(macInput);
    if (!canonical) {
      setLookupError('Enter a valid 12-character WiFi MAC address (e.g. AA:BB:CC:DD:EE:FF).');
      return;
    }
    setLookupError(null);
    lookupMutation.mutate(canonical);
  }

  const buildMutation = useMutation({
    mutationFn: async () => {
      if (!found) throw new Error('No device selected');
      // A powered-on device that hasn't entered the build phase yet gets moved
      // there first — it already has a MAC (we just looked it up by it), so the
      // in_build guard passes. registerBuild then auto-advances in_build → built.
      if (found.state === 'in_stock') {
        await api.adminTransitionStockState(found.id, 'in_build', 'Build started at build station');
      }
      const app_versions = appVersions.reduce<Record<string, string>>((acc, row) => {
        const k = row.key.trim();
        if (k) acc[k] = row.value.trim();
        return acc;
      }, {});
      return api.adminRegisterStockBuild(found.id, {
        built_by: builtBy.trim() || null,
        app_versions,
        git_sha: gitSha.trim() || null,
        android_version: androidVersion.trim() || null,
        notes: notes.trim() || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'inventory'] });
      if (found) {
        setSessionBuilds((s) => [
          { serial: found.serialNumber, product: found.product?.sku ?? '—', at: Date.now() },
          ...s,
        ]);
      }
      setFound(null);
      setLookupError(null);
    },
  });

  const buildable = found ? BUILDABLE_STATES.has(found.state) : false;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <WrenchScrewdriverIcon className="w-6 h-6 text-cyan-400" />
            <h1 className="text-2xl font-bold text-white">Build Station</h1>
          </div>
          <p className="text-slate-400 mt-1 text-sm">
            Scan a powered-on Scout's WiFi MAC to pair it with its inventory record and register the gold build.
          </p>
        </div>
        <Link
          to="/admin/inventory"
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white border border-slate-700 hover:border-slate-600 rounded-lg transition-colors shrink-0"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Inventory
        </Link>
      </div>

      {/* Lookup step */}
      {!found && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <label className="block text-sm font-medium text-white mb-2">WiFi MAC address</label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <WifiIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-500" />
              <input
                ref={macInputRef}
                value={macInput}
                onChange={(e) => setMacInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
                placeholder="AA:BB:CC:DD:EE:FF"
                autoComplete="off"
                spellCheck={false}
                className="w-full pl-10 pr-4 py-3 bg-slate-900 border-2 border-cyan-500/40 rounded-lg text-white text-lg font-mono tracking-wide focus:border-cyan-500 focus:outline-none placeholder-slate-600"
              />
            </div>
            <button
              onClick={handleLookup}
              disabled={lookupMutation.isPending || !macInput.trim()}
              className="flex items-center gap-2 px-5 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              {lookupMutation.isPending ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : <CpuChipIcon className="w-5 h-5" />}
              Look up
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Scan or type the MAC and press Enter. Separators and case don't matter.
          </p>

          {lookupError && (
            <div className="mt-4 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-200">{lookupError}</p>
            </div>
          )}
        </div>
      )}

      {/* Found device — identity + build form */}
      {found && (
        <div className="space-y-6">
          {/* Identity card */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <Link
                    to={`/admin/inventory/${found.id}`}
                    className="text-xl font-bold text-white font-mono hover:text-cyan-300 transition-colors"
                  >
                    {found.serialNumber}
                  </Link>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATE_COLORS[found.state]}`}>
                    {STATE_LABELS[found.state]}
                  </span>
                </div>
                <p className="text-slate-400 mt-1 text-sm">
                  {found.product?.name ?? 'Unknown product'} · {found.product?.sku ?? '—'}
                </p>
              </div>
              <button
                onClick={() => { setFound(null); setLookupError(null); }}
                className="text-sm text-slate-400 hover:text-white transition-colors shrink-0"
              >
                Look up another
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 mt-4">
              <Field label="WiFi MAC" value={found.wifiMacAddress ?? '—'} mono />
              {found.attributes?.model && <Field label="Model" value={found.attributes.model} />}
              {found.attributes?.imei1 && <Field label="IMEI 1" value={found.attributes.imei1} mono />}
              {found.attributes?.imei2 && <Field label="IMEI 2" value={found.attributes.imei2} mono />}
              {found.batchTag && <Field label="Batch" value={found.batchTag} />}
            </div>

            {found.latestBuild && (
              <div className="mt-4 flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                Already has a build on record ({new Date(found.latestBuild.builtAt).toLocaleString('en-NZ')}). Registering again records a re-flash.
              </div>
            )}
          </div>

          {/* Build form or not-applicable notice */}
          {buildable ? (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <WrenchScrewdriverIcon className="w-4 h-4 text-cyan-400" />
                Register Build
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Built by</label>
                  <input
                    value={builtBy}
                    onChange={(e) => setBuiltBy(e.target.value)}
                    placeholder="Technician name"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Android version</label>
                  <input
                    value={androidVersion}
                    onChange={(e) => setAndroidVersion(e.target.value)}
                    placeholder="e.g. 13.0"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Git SHA</label>
                <input
                  value={gitSha}
                  onChange={(e) => setGitSha(e.target.value)}
                  placeholder="Build commit hash"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono focus:border-cyan-500 focus:outline-none"
                />
              </div>

              {/* App versions */}
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">App versions</label>
                <div className="space-y-2">
                  {appVersions.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        value={row.key}
                        onChange={(e) => setAppVersions((rows) => rows.map((r, j) => j === i ? { ...r, key: e.target.value } : r))}
                        placeholder="app (e.g. vortexnav)"
                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                      />
                      <input
                        value={row.value}
                        onChange={(e) => setAppVersions((rows) => rows.map((r, j) => j === i ? { ...r, value: e.target.value } : r))}
                        placeholder="version (e.g. 3.77.0)"
                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono focus:border-cyan-500 focus:outline-none"
                      />
                      <button
                        onClick={() => setAppVersions((rows) => rows.length > 1 ? rows.filter((_, j) => j !== i) : rows)}
                        className="p-2 text-slate-500 hover:text-red-400 transition-colors"
                        title="Remove"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setAppVersions((rows) => [...rows, { key: '', value: '' }])}
                  className="mt-2 flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add app version
                </button>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Notes</label>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional build notes"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                />
              </div>

              {buildMutation.isError && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-200">
                    {(buildMutation.error as any)?.response?.data?.error ?? 'Failed to register build.'}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-1">
                <button
                  onClick={() => { setFound(null); setLookupError(null); }}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => buildMutation.mutate()}
                  disabled={buildMutation.isPending}
                  className="flex items-center gap-2 px-5 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg disabled:opacity-50 transition-colors"
                >
                  {buildMutation.isPending
                    ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Registering…</>
                    : <><CheckCircleIcon className="w-4 h-4" /> Complete Build</>}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
              <div className="flex items-start gap-2 text-sm text-amber-200">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <p>
                  This device is <span className="font-medium">{STATE_LABELS[found.state]}</span> — it's already past the
                  build phase, so a build can't be registered here. Open the{' '}
                  <Link to={`/admin/inventory/${found.id}`} className="text-cyan-400 hover:text-cyan-300 underline">item record</Link>{' '}
                  if you need to change its state.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Session log */}
      {sessionBuilds.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <CheckCircleIcon className="w-4 h-4 text-green-400" />
            Built this session ({sessionBuilds.length})
          </h2>
          <ul className="space-y-1.5">
            {sessionBuilds.map((b, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-400 shrink-0" />
                  <span className="font-mono text-slate-200">{b.serial}</span>
                  <span className="text-slate-500">· {b.product}</span>
                </span>
                <span className="text-xs text-slate-500">{new Date(b.at).toLocaleTimeString('en-NZ')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={`text-sm text-slate-200 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}
